// node modules
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useQuery, gql } from '@apollo/client';
import Skeleton from 'react-loading-skeleton';

// app modules: components
import { UnorderedList, ListItem } from 'components/List';

// app modules: services
import { isApp } from 'services/environment';

// assets
import CssMembershipBenefits from './MembershipBenefits.scss';

const CONTENT_QUERY = gql`
  query GetMembershipBenefits($id: String!) {
    MembershipBenefits(id: $id) {
      long_html
      short_html
      hideable
    }
  }
`;
function MembershipBenefits(props) {
  useStyles(CssMembershipBenefits);
  const { type, premium, itemClassName, className, compact, onLoad } = props;
  let id;
  if (type) {
    id = type;
  } else {
    id = premium ? 'premium' : 'free';
  }
  const { data, loading } = useQuery(CONTENT_QUERY, {
    variables: { id },
    fetchPolicy: isApp() ? 'cache-and-network' : 'cache-first',
    errorPolicy: 'all',
    onCompleted: () => setTimeout(onLoad, 0),
  });

  const listClasses = classNames(
    'membership-benefits',
    { 'list--ticked': data?.MembershipBenefits },
    className,
  );
  const hidableItemClasses = classNames(itemClassName, {
    'membership-benefits__item--hidable': compact,
  });

  if (loading && !data?.MembershipBenefits) {
    return (
      <UnorderedList className={listClasses} vertical spaceBetween="none">
        <ListItem className={itemClassName}>
          <Skeleton />
        </ListItem>
        <ListItem className={itemClassName}>
          <Skeleton />
        </ListItem>
        <ListItem className={itemClassName}>
          <Skeleton />
        </ListItem>
        <ListItem className={itemClassName}>
          <Skeleton />
        </ListItem>
        <ListItem className={itemClassName}>
          <Skeleton />
        </ListItem>
      </UnorderedList>
    );
  }

  if (!data?.MembershipBenefits) {
    return null;
  }

  return (
    <UnorderedList className={listClasses} vertical spaceBetween="none">
      {data.MembershipBenefits.map(benefit => (
        <ListItem
          key={benefit.long_html}
          className={benefit.hideable ? hidableItemClasses : itemClassName}
          dangerouslySetInnerHTML={{
            __html: compact ? benefit.short_html || benefit.long_html : benefit.long_html,
          }}
        />
      ))}
    </UnorderedList>
  );
}

MembershipBenefits.propTypes = {
  type: PropTypes.string,
  premium: PropTypes.bool,
  itemClassName: PropTypes.string,
  className: PropTypes.string,
  compact: PropTypes.bool,
  onLoad: PropTypes.func,
};

MembershipBenefits.defaultProps = {
  type: null,
  premium: false,
  itemClassName: '',
  className: 'margin-bottom-10',
  compact: false,
  onLoad: () => {},
};

export default React.memo(MembershipBenefits);
